/**
 * Logo Carousel — seamless auto-scrolling logo strip (Savor-native Logos3).
 * The motion is a CSS keyframe (translateX -50%) defined in the section's stylesheet, so it
 * runs in every browser. This component only (a) duplicates the logo set so the loop is seamless
 * and (b) reveals the heading. Pause-on-hover and reduced-motion are handled in CSS.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class LogoCarouselComponent extends Component {
  requiredRefs = ['track', 'set'];
  #io = null;

  connectedCallback() {
    super.connectedCallback();

    // Reveal the heading (works without scroll-timeline support).
    const reveals = this.querySelectorAll('.reveal');
    if (prefersReducedMotion()) {
      reveals.forEach((el) => el.classList.add('is-visible'));
    } else {
      this.#io = new IntersectionObserver((entries) => {
        for (const e of entries) if (e.isIntersecting) { e.target.classList.add('is-visible'); this.#io?.unobserve(e.target); }
      }, { rootMargin: '0px 0px -10% 0px' });
      reveals.forEach((el) => this.#io.observe(el));
    }

    // Seamless loop needs the set duplicated once; skip when reduced motion (CSS wraps instead).
    if (!prefersReducedMotion()) this.#ensureClone();
  }

  updatedCallback() {
    super.updatedCallback?.();
    if (!prefersReducedMotion()) this.#ensureClone();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#io?.disconnect();
  }

  #ensureClone() {
    const { track, set } = this.refs;
    if (!track || !set) return;
    // Only one real set should exist; (re)create exactly one aria-hidden clone.
    track.querySelectorAll('[data-lc-clone]').forEach((n) => n.remove());
    const clone = set.cloneNode(true);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('data-lc-clone', '');
    clone.removeAttribute('ref');
    track.appendChild(clone);
  }
}

if (!customElements.get('logo-carousel-component')) {
  customElements.define('logo-carousel-component', LogoCarouselComponent);
}
